import { useCallback, useMemo, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { PatientDemographicsPanel } from '@features/encounters/components/v2/PatientDemographicsPanel';
import { usePatientClinicalInfoQuery } from '@features/patients';
import { useAutoSave } from '@hooks/useAutoSave';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { Patient } from '@ts/types';

import {
  useGetEncounterEligibilityQuery,
  useGetPatientEligibilityQuery,
  useOverrideEligibilityMutation,
  useRunEligibilityCheckMutation,
  useRunPatientEligibilityCheckMutation,
  useUpdatePatientCoverageMutation,
} from '../../store/api';
import type {
  EligibilitySnapshot,
  ManualUpdateRequest,
} from '../../types';
import {
  apiToSnapshot,
  LIGHT_TO_API_STATUS,
} from '../../types';

import { CoverageDetailsGrid, type CoverageField, type CoverageOverrides } from './CoverageDetailsGrid';
import { formatAlertLabel, STATUS_CONFIG } from './eligibilityStatusConfig';
import { ManualOverrideFooter, type VerificationMethod } from './ManualOverrideFooter';

// ── Helpers ─────────────────────────────────────────────────────────────

function formatEligibilityStatus(status: string | undefined | null): { label: string; color: 'success' | 'error' | 'warning' } {
  if (!status) return { label: 'Unknown', color: 'error' };
  if (status === 'eligible') return { label: 'Active', color: 'success' };
  if (status === 'needs_review') return { label: 'Needs Review', color: 'warning' };
  if (status === 'blocked') return { label: 'Blocked', color: 'error' };
  return { label: 'Unknown', color: 'error' };
}

// ── Component ──────────────────────────────────────────────────────────

interface EligibilityBlockProps {
  patientId: string;
  encounterId?: string;
  defaultCollapsed?: boolean;
}

export function EligibilityBlock({
  patientId,
  encounterId,
}: EligibilityBlockProps) {
  const theme = useTheme();

  // ── API hooks ──────────────────────────────────────────────────────
  const {
    data: encounterApiData,
    isLoading: isEncounterLoading,
  } = useGetEncounterEligibilityQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  const {
    data: patientApiData,
    isLoading: isPatientLoading,
  } = useGetPatientEligibilityQuery(
    { patientId },
    { skip: !patientId || !!encounterId },
  );

  const apiData = encounterApiData ?? patientApiData;
  const isLoading = isEncounterLoading || isPatientLoading;

  const [overrideEligibility] = useOverrideEligibilityMutation();

  // Re-run mutations
  const [triggerEncounterCheck, { isLoading: isEncounterRerunning }] =
    useRunEligibilityCheckMutation();
  const [triggerPatientCheck, { isLoading: isPatientRerunning }] =
    useRunPatientEligibilityCheckMutation();
  const isRerunning = isEncounterRerunning || isPatientRerunning;

  const handleRerun = useCallback(() => {
    if (encounterId) {
      triggerEncounterCheck({ encounterId });
    } else {
      triggerPatientCheck({ patientId });
    }
  }, [encounterId, patientId, triggerEncounterCheck, triggerPatientCheck]);

  // ── Member ID correction ────────────────────────────────────────────
  const [updateCoverage, { isLoading: isUpdatingMemberId }] =
    useUpdatePatientCoverageMutation();
  const [memberIdCorrected, setMemberIdCorrected] = useState(false);

  const handleApplyMemberIdCorrection = useCallback(async () => {
    const coverageId = apiData?.patient_insurance_coverage_id;
    const correctedId = apiData?.corrected_member_id;
    if (!coverageId || !correctedId) return;

    try {
      await updateCoverage({
        patientId,
        coverageId,
        updateCoverageRequest: { member_id: correctedId },
      }).unwrap();
      setMemberIdCorrected(true);
    } catch {
      // Error is surfaced by RTK Query — no additional handling needed
    }
  }, [apiData?.patient_insurance_coverage_id, apiData?.corrected_member_id, patientId, updateCoverage]);

  // Demographics — shown when editing in override mode with demographics alert
  const hasDemographicsAlert = useMemo(() => {
    const alerts = apiData?.alerts ?? [];
    return alerts.some(a => a.toUpperCase().includes('DEMOGRAPHICS'));
  }, [apiData?.alerts]);

  const { data: clinicalInfoResponse, isLoading: isDemoLoading } =
    usePatientClinicalInfoQuery({ patientId }, { skip: !patientId || !hasDemographicsAlert });

  const patient = useMemo((): Patient | null => {
    if (!clinicalInfoResponse?.patient_clinical_info) return null;
    const info = clinicalInfoResponse.patient_clinical_info;
    return {
      patient_id: info.patient_id,
      first_name: info.first_name ?? null,
      last_name: info.last_name ?? null,
      preferred_name: info.preferred_name ?? null,
      middle_name: info.middle_name ?? null,
      dob: info.dob ?? null,
      gender: info.gender ?? null,
      pronouns: info.pronouns ?? null,
      email: info.email ?? null,
      mobile_phone: info.mobile_phone ?? null,
      phone: info.mobile_phone ?? null,
    } as Patient;
  }, [clinicalInfoResponse]);

  // ── Local state ────────────────────────────────────────────────────
  const [localSnapshot, setLocalSnapshot] = useState<EligibilitySnapshot | null>(null);
  const [hasLocalOverride, setHasLocalOverride] = useState(false);
  const [isOverrideMode, setIsOverrideMode] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>('');
  const [coverageOverrides, setCoverageOverrides] = useState<CoverageOverrides>({});
  const pendingDataRef = useRef<EligibilitySnapshot | null>(null);

  // Derive the effective snapshot — API data wins unless user has local edits
  const snapshot = useMemo<EligibilitySnapshot | null>(() => {
    if (hasLocalOverride && localSnapshot) return localSnapshot;
    if (apiData) return apiToSnapshot(apiData);
    return localSnapshot;
  }, [hasLocalOverride, localSnapshot, apiData]);

  // ── Autosave ───────────────────────────────────────────────────────
  const performSave = useCallback(
    async (data: EligibilitySnapshot) => {
      if (!encounterId) return;
      const manualUpdateRequest: ManualUpdateRequest = {
        status: LIGHT_TO_API_STATUS[data.status_light],
        summary: data.summary,
        ...(verificationMethod ? { method: verificationMethod } : {}),
        ...coverageOverrides,
      };
      await overrideEligibility({ encounterId, manualUpdateRequest }).unwrap();
    },
    [encounterId, overrideEligibility, verificationMethod, coverageOverrides],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } =
    useAutoSave<EligibilitySnapshot>({
      onSave: performSave,
      debounceMs: 2000,
      flushOnUnmount: true,
    });

  const handleOverrideChange = useCallback(
    (field: 'status_light' | 'summary', value: string) => {
      if (!snapshot) return;
      const updated = { ...snapshot, [field]: value };
      setLocalSnapshot(updated);
      setHasLocalOverride(true);
      pendingDataRef.current = updated as EligibilitySnapshot;

      if (field === 'summary') {
        triggerAutoSave(updated);
      } else {
        saveImmediately(updated);
      }
    },
    [snapshot, triggerAutoSave, saveImmediately],
  );

  const handleCoverageFieldChange = useCallback(
    (field: CoverageField, value: string | number | null) => {
      if (!snapshot) return;
      setCoverageOverrides((prev) => ({ ...prev, [field]: value }));
      setHasLocalOverride(true);
      const current = pendingDataRef.current ?? snapshot;
      pendingDataRef.current = current;
      triggerAutoSave(current);
    },
    [snapshot, triggerAutoSave],
  );

  const handleBlur = useCallback(() => {
    if (pendingDataRef.current) {
      saveImmediately(pendingDataRef.current);
      pendingDataRef.current = null;
    }
  }, [saveImmediately]);

  const handleToggleOverride = useCallback(() => {
    setIsOverrideMode((prev) => !prev);
  }, []);

  // ── Derived display values ─────────────────────────────────────────
  const coverageStatus = formatEligibilityStatus(apiData?.status);
  const alerts = snapshot?.alerts ?? [];

  // ── Render ─────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={24} width={200} />
          <Skeleton variant="rounded" height={16} width={140} />
        </Stack>
      </Paper>
    );
  }

  if (!snapshot || !apiData) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Eligibility
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No eligibility data available. Run an eligibility check from an encounter to see
          results here.
        </Typography>
      </Paper>
    );
  }

  const statusColor = theme.palette[coverageStatus.color].main;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(statusColor, 0.4),
        overflow: 'hidden',
      }}
    >
      {/* ── 1. Coverage Status ─────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: statusColor,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            Coverage: {coverageStatus.label}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutosaveStatus
            status={status}
            isDirty={pendingDataRef.current !== null}
            lastSavedAt={lastSavedAt}
            verbose
          />
          <Button
            variant="text"
            size="small"
            onClick={handleRerun}
            disabled={isRerunning}
            startIcon={isRerunning ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: '0.8125rem', minWidth: 'auto' }}
          >
            Re-run
          </Button>
        </Box>
      </Box>

      {/* ── 2. Action Items (from alerts) ──────────────────────────── */}
      {alerts.length > 0 && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
            Needs attention:
          </Typography>
          <Stack spacing={0.5}>
            {alerts.map((alert) => (
              <Box key={alert} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: 'text.disabled',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {formatAlertLabel(alert)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* ── 2b. Member ID Correction Banner ─────────────────────── */}
      {apiData?.corrected_member_id && !memberIdCorrected && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleApplyMemberIdCorrection}
                disabled={isUpdatingMemberId}
                startIcon={isUpdatingMemberId ? <CircularProgress size={14} /> : undefined}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Update Coverage
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Subscriber ID differs from payer records
            </Typography>
            <Typography variant="body2">
              On file: <strong>{apiData.submitted_member_id ?? '—'}</strong>
              {' → '}
              Payer returned: <strong>{apiData.corrected_member_id}</strong>
            </Typography>
          </Alert>
        </Box>
      )}
      {apiData?.corrected_member_id && memberIdCorrected && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />}>
            Subscriber ID updated to <strong>{apiData.corrected_member_id}</strong>
          </Alert>
        </Box>
      )}

      {/* ── 3. Update Info Action ──────────────────────────────────── */}
      {!isOverrideMode && (
        <Box
          sx={{
            px: 2.5,
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={handleToggleOverride}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Update info
          </Button>
        </Box>
      )}

      {/* ── Override Mode (expanded) ───────────────────────────────── */}
      <Collapse in={isOverrideMode} unmountOnExit>
        <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
          {/* Demographics fix (if applicable) */}
          {hasDemographicsAlert && (
            <Box
              sx={{
                bgcolor: alpha(theme.palette.warning.light, 0.1),
                borderRadius: 1,
                p: 2,
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, 0.3),
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Review Patient Demographics
              </Typography>
              <PatientDemographicsPanel
                patient={patient}
                isLoading={isDemoLoading}
              />
            </Box>
          )}

          {/* Coverage details + financial overrides */}
          <CoverageDetailsGrid
            data={apiData}
            isOverrideMode={isOverrideMode}
            overrides={coverageOverrides}
            onFieldChange={handleCoverageFieldChange}
            onBlur={handleBlur}
          />

          {/* Manual override — own card */}
          <ManualOverrideFooter
            snapshot={snapshot}
            verificationMethod={verificationMethod}
            onToggleOverride={handleToggleOverride}
            onOverrideChange={handleOverrideChange}
            onMethodChange={setVerificationMethod}
            onBlur={handleBlur}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}
